import {
  Group,
  Stack,
  Text,
  Button,
  Modal,
  Select,
  Slider,
  TextInput,
  NumberInput,
  Input,
  Badge,
  Flex,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  IconPencil,
  IconClockPause,
  IconClockPlay,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  HealthCheck,
  ScheduleType,
  Schedule,
} from "../utils/api/api_types";
import { ScheduleTypeDisplay } from "../utils/enum_records";
import coreApi from "../utils/api/coreApi";
import useApiSubmit from "../utils/api/useApiSubmit";
import { useState } from "react";
import {
  toggleEnableHealthCheck,
  runHealthCheck,
} from "../utils/health_checks";
import { STANDARD_ERROR_MESSAGE } from "../utils/constants";
import { MOBILE_VIEW_MAX_WIDTH_EM } from "../Settings";


type ScheduleFormValues = {
  schedule_type: ScheduleType | null | undefined;
  timeout_sec: number | null | undefined;
  minutes: number | null | undefined;
  cron: string;
  next_run: Date | null;
};

const HealthCheckSchedule = ({
  healthCheck,
  onScheduleUpdated,
  onManualTrigger,
}: {
  healthCheck: HealthCheck;
  onScheduleUpdated: () => void;
  onManualTrigger: () => void;
}) => {
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_VIEW_MAX_WIDTH_EM})`);

  const schedule = healthCheck.schedule;
  const [opened, { open, close }] = useDisclosure(false);
  const timeoutConfig = healthCheck.schedule_timeout_config;
  const maxTimeoutSecDefault = timeoutConfig.max_timeout_sec
  const maxCronTimeoutSec = timeoutConfig.max_cron_timeout_sec;

  const getInitialValues = (schedule: Schedule | null): ScheduleFormValues => ({
    schedule_type: schedule?.schedule_type,
    timeout_sec: schedule?.timeout_sec,
    minutes: schedule?.minutes,
    cron: schedule?.cron || "",
    next_run: schedule?.next_run ? new Date(schedule?.next_run) : null,
  });

  const form = useForm({
    initialValues: getInitialValues(schedule),
    transformValues: (values) => {
      const formatDate = (date: Date) => {
        const pad = (n: number) => n.toString().padStart(2, "0");
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const day = date.getDate();
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        return `${hours}:${minutes} ${day}/${month}/${year}`;
      };
      switch (values.schedule_type) {
        case ScheduleType.MINUTES:
          return {
            schedule_type: values.schedule_type,
            timeout_sec: values.timeout_sec,
            minutes: values.minutes,
          };
        case ScheduleType.CRON:
          return {
            schedule_type: values.schedule_type,
            timeout_sec: values.timeout_sec,
            cron: values.cron,
          };
        default:
          return {
            schedule_type: values.schedule_type,
            timeout_sec: values.timeout_sec,
            next_run: values.next_run ? formatDate(values.next_run) : null,
          };
      }
    },
  });

  const maxTimeoutSec =
    form.values.schedule_type === ScheduleType.CRON
      ? maxCronTimeoutSec
      : maxTimeoutSecDefault;

  const { loading, handleSubmit, nonFieldErrors, resetErrors } = useApiSubmit({
    apiFunc: (data) =>
      coreApi.healthChecksCreateOrUpdateSchedule(healthCheck.id, data),
    form,
    onSuccess: () => {
      onScheduleUpdated();
      close();
    },
  });

  const getComparableSchedule = (s: ScheduleFormValues | Schedule | null) => {
    if (!s) return null;

    switch (s.schedule_type) {
      case ScheduleType.MINUTES:
        return {
          schedule_type: s.schedule_type,
          timeout_sec: s.timeout_sec,
          minutes: s.minutes,
        };
      case ScheduleType.CRON:
        return {
          schedule_type: s.schedule_type,
          timeout_sec: s.timeout_sec,
          cron: s.cron,
        };
      default:
        return {
          schedule_type: s.schedule_type,
          timeout_sec: s.timeout_sec,
          next_run: s.next_run ? new Date(s.next_run) : null,
        };
    }
  };

  const updateDisabled =
    JSON.stringify(getComparableSchedule(form.values)) ===
    JSON.stringify(getComparableSchedule(schedule));

  const action = schedule ? "Update" : "Create";
  const handleOpen = () => {
    resetErrors();
    form.setValues(getInitialValues(schedule));
    open();
  };

  const [loadingEnable, setLoadingEnable] = useState(false);
  const handleToggleEnable = async () => {
    try {
      setLoadingEnable(true);
      await toggleEnableHealthCheck(healthCheck.id);
      onScheduleUpdated();
    } catch {
      notifications.show({
        title: "Error occurred!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingEnable(false);
    }
  };

  const [loadingTrigger, setLoadingTrigger] = useState(false);
  const handleTrigger = async () => {
    try {
      setLoadingTrigger(true);
      await runHealthCheck(healthCheck.id);
      onManualTrigger();
    } catch {
      notifications.show({
        title: "Error occurred!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingTrigger(false);
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        centered
        onClose={() => {
          resetErrors();
          close();
        }}
        title={`${action} schedule`}
      >
        <form
          onSubmit={(e) => {
            resetErrors();
            e.preventDefault(); // stop page reload
            form.onSubmit(handleSubmit)(e);
          }}
        >
          <Stack gap="xl">
            <Select
              label="Schedule type"
              {...form.getInputProps("schedule_type")}
              data={Object.entries(ScheduleType)
                .map(([_, value]) => ({
                  value: value,
                  label: ScheduleTypeDisplay[value],
                }))
                .filter((item) => item.value !== ScheduleType.ONCE)}
            />
            {/* Conditional Inputs */}
            {!form.values.schedule_type ? (
              <></>
            ) : form.values.schedule_type === ScheduleType.CRON ? (
              <TextInput
                label="CRON expression"
                placeholder="e.g. */5 * * * *"
                {...form.getInputProps("cron")}
              />
            ) : form.values.schedule_type === ScheduleType.MINUTES ? (
              <NumberInput
                label="Frequency minutes"
                {...form.getInputProps("minutes")}
              />
            ) : (
              <DateTimePicker
                {...form.getInputProps("next_run")}
                label="Next Run Date Time"
                placeholder="Select date and time"
              />
            )}
            <Input.Wrapper
              label="Timeout seconds"
              error={form.errors.timeout_sec}
            >
              <Slider
                min={10}
                max={maxTimeoutSec}
                step={1}
                label={(value) => `${value}s`}
                {...form.getInputProps("timeout_sec")}
              />
            </Input.Wrapper>
            <Text size="xs" c="dimmed" mt={-12}>
              {form.values.schedule_type === ScheduleType.CRON
                ? "CRON schedules must stay under 60 seconds."
                : `Timeout can be up to ${maxTimeoutSecDefault} seconds.`}
            </Text>
            {nonFieldErrors && nonFieldErrors.length > 0 ? (
              <Text size="sm" c="red" my="md">
                {nonFieldErrors}
              </Text>
            ) : (
              <></>
            )}

            <Button
              color="brand.5"
              disabled={updateDisabled}
              loading={loading}
              type="submit"
              variant="filled"
            >
              {action}
            </Button>
          </Stack>
        </form>
      </Modal>

      <Group align="flex-start">
        <Stack w="100%" gap={5}>
          <Flex
            gap={isMobile ? 5 : "md"}
            justify="flex-start"
            align={isMobile ? "flex-start" : "center"}
            direction={isMobile ? "column" : "row"}
          >
            <Text>
              <b>Schedule: </b>
              {schedule?.schedule_display
                ? `${schedule.schedule_display}`
                : "No Schedule"}
            </Text>
            <Button
              color="brand.5"
              type="submit"
              variant="light"
              leftSection={<IconPencil />}
              onClick={handleOpen}
              size={isMobile ? "sm" : "compact-sm"}
              fullWidth={isMobile}
            >
              {action}
            </Button>
          </Flex>
          {schedule && (
            <Flex
              gap={isMobile ? 5 : "md"}
              justify="flex-start"
              align={isMobile ? "flex-start" : "center"}
              direction={isMobile ? "column" : "row"}
            >
              <Text>
                <b>Next Run: </b>
                {healthCheck.enabled == true ? (
                  schedule.next_run_formatted
                ) : (
                  <Badge color="yellow">Paused</Badge>
                )}
              </Text>
              <Button
                color={healthCheck.enabled ? "yellow" : "green"}
                variant="light"
                leftSection={
                  healthCheck.enabled ? <IconClockPause /> : <IconClockPlay />
                }
                onClick={handleToggleEnable}
                loading={loadingEnable}
                size={isMobile ? "sm" : "compact-sm"}
                fullWidth={isMobile}
              >
                {healthCheck.enabled ? "Pause Schedule" : "Resume Schedule"}
              </Button>
              <Button
                color="teal"
                variant="light"
                leftSection={<IconPlayerPlay />}
                onClick={handleTrigger}
                loading={loadingTrigger}
                size={isMobile ? "sm" : "compact-sm"}
                fullWidth={isMobile}
              >
                Trigger Manually
              </Button>
            </Flex>
          )}
        </Stack>
      </Group>
    </>
  );
};

export default HealthCheckSchedule;
